import { defineStore } from 'pinia';
import { ref } from 'vue';
import { api } from 'boot/axios';

// Union real espejo de asupca-api/src/db/schema/courses.ts — fuente de verdad del front.
// Cada componente de bloque puede definir una interface local mas precisa para sus props.
export type ContentBlock =
  | { type: 'heading'; level: 1 | 2 | 3; text: string }
  | { type: 'paragraph'; markdown: string }
  | { type: 'list'; style: 'bullet' | 'numbered'; items: string[] }
  | { type: 'callout'; variant: 'info' | 'warning' | 'tip' | 'example' | 'key'; title?: string; text: string }
  | { type: 'quote'; text: string; attribution?: string }
  | { type: 'code'; language?: string; code: string }
  | { type: 'divider' }
  | { type: 'image'; src: string; alt?: string; caption?: string }
  | { type: 'toggle'; title: string; content: string }
  | { type: 'key_points'; title?: string; points: string[] }
  | { type: 'grid'; columns: 2 | 3 | 4; cards: Array<{ icon?: string; title: string; body: string; accent?: string }> }
  | { type: 'comparison'; title?: string; correct: { label?: string; items: string[] }; incorrect: { label?: string; items: string[] } }
  | { type: 'quiz'; questions: Array<{ question: string; options: string[]; correctIndex: number; explanation?: string }> };

export interface AdminLesson {
  id: string;
  title: string;
  orderIndex: number;
  videoUrl: string | null;
  richContent: ContentBlock[] | null;
  description: string | null;
  estimatedMinutes: number | null;
}

export interface AdminUnit {
  id: string;
  title: string;
  orderIndex: number;
  description?: string | null;
  isPublished?: boolean;
  lessons: AdminLesson[];
}

export interface AdminCourse {
  id: string;
  title: string;
  slug: string;
  status: 'draft' | 'published' | 'archived';
  shortDescription: string | null;
  description: string | null;
  coverImageUrl: string | null;
  isFree: boolean;
  priceArs: string | null;
  certLevel: string | null;
  createdAt: string;
  units?: AdminUnit[];
}

export interface CreateCourseBody {
  title: string;
  slug: string;
  shortDescription?: string;
  description?: string;
  coverImageUrl?: string;
  status?: 'draft' | 'published' | 'archived';
}

export interface UpdateCourseBody {
  title?: string;
  slug?: string;
  shortDescription?: string;
  description?: string;
  coverImageUrl?: string;
  status?: 'draft' | 'published' | 'archived';
}

export const useAdminCoursesStore = defineStore('admin-courses', () => {
  // =========================================================================
  // Course list state
  // =========================================================================
  const courseList = ref<AdminCourse[]>([]);
  const listLoading = ref(false);
  const listError = ref<string | null>(null);

  // =========================================================================
  // Editing course state (full tree)
  // =========================================================================
  const editingCourse = ref<AdminCourse | null>(null);
  const editLoading = ref(false);
  const editError = ref<string | null>(null);

  // =========================================================================
  // Rich content working state (for lesson editor — plan 04)
  // =========================================================================
  const editingLessonId = ref<string | null>(null);
  const richContent = ref<ContentBlock[]>([]);
  const isDirty = ref(false);

  // =========================================================================
  // Course CRUD actions
  // =========================================================================

  async function fetchCourses(): Promise<void> {
    listLoading.value = true;
    listError.value = null;
    try {
      const { data } = await api.get<AdminCourse[]>('/admin/courses');
      courseList.value = data;
    } catch (err: unknown) {
      listError.value = err instanceof Error ? err.message : 'Error al cargar cursos';
    } finally {
      listLoading.value = false;
    }
  }

  async function fetchCourse(courseId: string): Promise<void> {
    editLoading.value = true;
    editError.value = null;
    try {
      const { data } = await api.get<AdminCourse>(`/admin/courses/${courseId}`);
      editingCourse.value = data;
    } catch (err: unknown) {
      editError.value = err instanceof Error ? err.message : 'Error al cargar el curso';
    } finally {
      editLoading.value = false;
    }
  }

  async function createCourse(body: CreateCourseBody): Promise<{ id: string; slug: string }> {
    const { data } = await api.post<{ id: string; slug: string }>('/admin/courses', body);
    return data;
  }

  async function updateCourse(courseId: string, body: UpdateCourseBody): Promise<void> {
    await api.put(`/admin/courses/${courseId}`, body);
    // Sync local state if the editing course is this one
    if (editingCourse.value && editingCourse.value.id === courseId) {
      Object.assign(editingCourse.value, body);
    }
    // Sync list entry
    const listItem = courseList.value.find((c) => c.id === courseId);
    if (listItem) {
      Object.assign(listItem, body);
    }
  }

  async function deleteCourse(courseId: string): Promise<void> {
    await api.delete(`/admin/courses/${courseId}`);
    courseList.value = courseList.value.filter((c) => c.id !== courseId);
    if (editingCourse.value?.id === courseId) {
      editingCourse.value = null;
    }
  }

  // =========================================================================
  // Unit actions
  // =========================================================================

  async function createUnit(courseId: string, title: string): Promise<{ id: string; orderIndex: number }> {
    const { data } = await api.post<{ id: string; orderIndex: number }>(
      `/admin/courses/${courseId}/units`,
      { title },
    );
    // Append to in-memory course tree
    if (editingCourse.value && editingCourse.value.id === courseId) {
      if (!editingCourse.value.units) editingCourse.value.units = [];
      editingCourse.value.units.push({ id: data.id, title, orderIndex: data.orderIndex, lessons: [] });
    }
    return data;
  }

  async function updateUnit(unitId: string, title: string): Promise<void> {
    await api.put(`/admin/units/${unitId}`, { title });
    if (editingCourse.value?.units) {
      const unit = editingCourse.value.units.find((u) => u.id === unitId);
      if (unit) unit.title = title;
    }
  }

  async function deleteUnit(unitId: string): Promise<void> {
    await api.delete(`/admin/units/${unitId}`);
    if (editingCourse.value?.units) {
      editingCourse.value.units = editingCourse.value.units
        .filter((u) => u.id !== unitId)
        .map((u, idx) => ({ ...u, orderIndex: idx }));
    }
  }

  async function reorderUnits(courseId: string, items: Array<{ id: string; orderIndex: number }>): Promise<void> {
    await api.patch(`/admin/courses/${courseId}/units/reorder`, { items });
    if (editingCourse.value?.units) {
      items.forEach(({ id, orderIndex }) => {
        const unit = editingCourse.value!.units!.find((u) => u.id === id);
        if (unit) unit.orderIndex = orderIndex;
      });
      editingCourse.value.units.sort((a, b) => a.orderIndex - b.orderIndex);
    }
  }

  // =========================================================================
  // Lesson actions
  // =========================================================================

  async function createLesson(unitId: string, title: string): Promise<{ id: string; orderIndex: number }> {
    const { data } = await api.post<{ id: string; orderIndex: number }>(
      `/admin/units/${unitId}/lessons`,
      { title },
    );
    if (editingCourse.value?.units) {
      const unit = editingCourse.value.units.find((u) => u.id === unitId);
      if (unit) {
        unit.lessons.push({ id: data.id, title, orderIndex: data.orderIndex, videoUrl: null, richContent: null, description: null, estimatedMinutes: null });
      }
    }
    return data;
  }

  async function updateLesson(
    lessonId: string,
    body: { title?: string; description?: string; estimatedMinutes?: number },
  ): Promise<void> {
    await api.put(`/admin/lessons/${lessonId}`, body);
    if (editingCourse.value?.units) {
      for (const unit of editingCourse.value.units) {
        const lesson = unit.lessons.find((l) => l.id === lessonId);
        if (lesson) {
          if (body.title !== undefined) lesson.title = body.title;
          break;
        }
      }
    }
  }

  async function deleteLesson(lessonId: string): Promise<void> {
    await api.delete(`/admin/lessons/${lessonId}`);
    if (editingCourse.value?.units) {
      for (const unit of editingCourse.value.units) {
        const before = unit.lessons.length;
        unit.lessons = unit.lessons
          .filter((l) => l.id !== lessonId)
          .map((l, idx) => ({ ...l, orderIndex: idx }));
        if (unit.lessons.length !== before) break;
      }
    }
  }

  async function reorderLessons(unitId: string, items: Array<{ id: string; orderIndex: number }>): Promise<void> {
    await api.patch(`/admin/units/${unitId}/lessons/reorder`, { items });
    if (editingCourse.value?.units) {
      const unit = editingCourse.value.units.find((u) => u.id === unitId);
      if (unit) {
        items.forEach(({ id, orderIndex }) => {
          const lesson = unit.lessons.find((l) => l.id === id);
          if (lesson) lesson.orderIndex = orderIndex;
        });
        unit.lessons.sort((a, b) => a.orderIndex - b.orderIndex);
      }
    }
  }

  /** Actualiza el videoUrl de una leccion en el arbol en memoria (tras subir/eliminar). */
  function setLessonVideo(lessonId: string, videoUrl: string | null): void {
    if (!editingCourse.value?.units) return;
    for (const unit of editingCourse.value.units) {
      const lesson = unit.lessons.find((l) => l.id === lessonId);
      if (lesson) {
        lesson.videoUrl = videoUrl;
        break;
      }
    }
  }

  // =========================================================================
  // Rich content working-state actions (consumed by plan 04 lesson editor)
  // =========================================================================

  function setBlocks(blocks: ContentBlock[]): void {
    richContent.value = [...blocks];
    isDirty.value = false;
  }

  function addBlock(block: ContentBlock): void {
    richContent.value.push(block);
    isDirty.value = true;
  }

  function updateBlock(index: number, block: ContentBlock): void {
    richContent.value.splice(index, 1, block);
    isDirty.value = true;
  }

  function removeBlock(index: number): void {
    richContent.value.splice(index, 1);
    isDirty.value = true;
  }

  function moveBlock(from: number, to: number): void {
    const [item] = richContent.value.splice(from, 1);
    richContent.value.splice(to, 0, item);
    isDirty.value = true;
  }

  async function saveContent(lessonId: string): Promise<void> {
    // PUT /admin/lessons/:id/content — endpoint built in plan 04
    await api.put(`/admin/lessons/${lessonId}/content`, {
      richContent: richContent.value,
    });
    isDirty.value = false;
  }

  return {
    // Course list
    courseList,
    listLoading,
    listError,
    // Editing course
    editingCourse,
    editLoading,
    editError,
    // Rich content working state
    editingLessonId,
    richContent,
    isDirty,
    // Actions
    fetchCourses,
    fetchCourse,
    createCourse,
    updateCourse,
    deleteCourse,
    // Unit actions
    createUnit,
    updateUnit,
    deleteUnit,
    reorderUnits,
    // Lesson actions
    createLesson,
    updateLesson,
    deleteLesson,
    reorderLessons,
    setLessonVideo,
    // Rich content working state
    setBlocks,
    addBlock,
    updateBlock,
    removeBlock,
    moveBlock,
    saveContent,
  };
});
